<template>
  <template v-if="domReady">
    <Teleport :to="to">
      <div
        ref="boxEl"
        :class="{
          'simple-info': true,
          'simple-info-visible-100': p100,
          'simple-info-visible-0': !p100,
          [position]: visible,
        }"
        @click.stop="() => {}"
      >
        <div class="simple-info-title">
          <span>{{ title }}</span>
          <img :src="CloseIcon" class="close" alt="close" @click="hide" />
        </div>
        <div class="simple-info-content">
          <div v-for="(value, key) in dataRef" :key="key" class="simple-info-item">
            <span class="simple-info-item-key">{{ keyMapRef[key] || key }}：</span>
            <span class="simple-info-item-value">{{ value }}</span>
          </div>
        </div>
      </div>
    </Teleport>
  </template>
</template>
<script setup lang="ts">
import { ref, getCurrentInstance, inject, onMounted, watch, nextTick } from "vue"
import CloseIcon from "@p-v/asset/image/icon_close_white.png"
import { v4 as uuid } from "uuid"

const componentId = uuid()
const install = inject("install") as (ins: any) => void
const bmap = inject("bmap") as any
const infoBoxId = inject("infoBoxId") as string
const markInfoBoxActiveComponentId = inject("markInfoBoxActiveComponentId") as any
const domReady = ref(false)

const instance = getCurrentInstance()

interface ComponentProps {
  title: string
  keyMap: Record<string, string>
  data: any
  position: "top" | "bottom" | "left" | "right"
  hideIfOtherClick?: boolean
}

const to = ref<string>(`#${infoBoxId}`)
const visibleAnimate = ref(false)
const visible = ref(false)
const p100 = ref(false)

const props = withDefaults(defineProps<ComponentProps>(), {
  title: () => "",
  keyMap: () => ({}),
  data: () => ({}),
  position: () => "top",
  hideIfOtherClick: () => true,
})

const keyMapRef = ref(props.keyMap)
const dataRef = ref(props.data)
const boxEl = ref<HTMLElement | null>(null)

const setData = (data: any, keyMap?: any) => {
  dataRef.value = data
  if (keyMap) {
    keyMapRef.value = keyMap
  }
}

const show = () => {
  markInfoBoxActiveComponentId.value = componentId
  visibleAnimate.value = true
}

const hide = () => {
  visibleAnimate.value = false
}

if (props.hideIfOtherClick) {
  watch(
    () => markInfoBoxActiveComponentId.value,
    value => {
      if (value !== componentId) {
        hide()
      }
    },
  )
}

watch(
  () => visibleAnimate.value,
  value => {
    if (value) {
      visible.value = true
      p100.value = true
    } else {
      p100.value = false
      visible.value = false
    }
  },
)

defineExpose({
  boxEl,
  setData,
  show,
  hide,
  componentId,
})

watch(
  () => bmap.value,
  value => {
    if (value) {
      install(instance)
    }
  },
  { immediate: true },
)

const checkDom = () => {
  if (document.getElementById(infoBoxId)) {
    nextTick(() => {
      domReady.value = true
    })
  }
  if (!domReady.value) {
    const timer = setInterval(() => {
      if (document.getElementById(infoBoxId)) {
        clearInterval(timer)
        nextTick(() => {
          domReady.value = true
        })
      }
    }, 200)
  }
}

onMounted(() => {
  // 校验infoBoxId的Dom是否存在
  checkDom()
})
</script>
<style lang="scss" scoped>
.simple-info {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 999;
  background: linear-gradient(to bottom, rgb(11 45 64), rgba(38, 135, 220, 0.8));
  border: 2px solid rgb(38 135 220);
  color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  opacity: 0;
  overflow: hidden;
  transition: all 0.3s;
  box-sizing: border-box;
  transform: translateY(0) translateX(0);
  &.simple-info-visible-100 {
    width: 250px;
    height: 170px;
    border-radius: 4px;
    padding: 10px;
    opacity: 1;
  }
  &.simple-info-visible-0 {
    width: 1px;
    height: 1px;
    border-radius: 0;
    padding: 0;
    opacity: 0;
  }
  &.top {
    left: 50%;
    top: -10px;
    transform: translateY(-100%) translateX(-50%);
  }
  &.bottom {
    left: 50%;
    bottom: -10px;
    transform: translateY(100%) translateX(-50%);
  }
  &.left {
    top: 50%;
    left: -10px;
    transform: translateY(-50%) translateX(-100%);
  }
  &.right {
    top: 50%;
    right: -10px;
    transform: translateY(-50%) translateX(100%);
  }
  .simple-info-title {
    font-size: 14px;
    font-weight: bold;
    padding-bottom: 5px;
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: #707070 1px solid;
    .close {
      cursor: pointer;
      width: 14px;
      height: 14px;
    }
  }
  .simple-info-content {
    display: flex;
    flex-direction: column;
    justify-items: flex-start;
    align-items: flex-start;
    .simple-info-item {
      margin-bottom: 4px;
      font-size: 12px;
      .simple-info-item-key {
        font-weight: bold;
      }
    }
  }
}
</style>
